export interface RecentLeadImport {
	emails: string[];
	completedAt: number;
	dismissed?: boolean;
	completed?: boolean; // true when import fully completes, false during incremental saves
	totalImported?: number;
}

export interface LeadImportProgress {
	processed: number;
	total: number;
	inProgress: boolean;
	updatedAt: number;
	sessionId?: string;
}

const STORAGE_PREFIX = "lead-import-recent";
const PROGRESS_PREFIX = "lead-import-progress";
const CANCEL_PREFIX = "lead-import-cancel";
const MAX_EMAILS = 500;
const PAGE_SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getKey = (userId: number) => `${STORAGE_PREFIX}:${userId}`;
const getProgressKey = (userId: number) => `${PROGRESS_PREFIX}:${userId}`;
const getCancelKey = (userId: number) => `${CANCEL_PREFIX}:${userId}`;

const normalizeEmails = (emails: string[]) => {
	const unique = Array.from(
		new Set(
			emails
				.map((email) => (email || "").trim().toLowerCase())
				.filter(Boolean)
		)
	);
	return unique.slice(0, MAX_EMAILS);
};

export const getRecentLeadImport = (userId: number): RecentLeadImport | null => {
	try {
		const raw = window.localStorage.getItem(getKey(userId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as RecentLeadImport;
		if (!parsed || !Array.isArray(parsed.emails)) return null;
		const totalImported =
			typeof parsed.totalImported === "number"
				? parsed.totalImported
				: parsed.emails.length;
		return {
			...parsed,
			totalImported,
			emails: normalizeEmails(parsed.emails),
		};
	} catch{
		return null;
	}
};

export const setRecentLeadImport = (userId: number, data: RecentLeadImport) => {
	try {
		const totalImported =
			typeof data.totalImported === "number"
				? data.totalImported
				: data.emails.length;
		const payload: RecentLeadImport = {
			...data,
			totalImported,
			emails: normalizeEmails(data.emails),
		};
		window.localStorage.setItem(getKey(userId), JSON.stringify(payload));
	} catch{
		// Ignore write errors (storage disabled or unavailable)
	}
};

export const dismissRecentLeadImport = (userId: number) => {
	const current = getRecentLeadImport(userId);
	if (!current) return;
	setRecentLeadImport(userId, { ...current, dismissed: true });
};

export const clearRecentLeadImport = (userId: number) => {
	try {
		window.localStorage.removeItem(getKey(userId));
	} catch{
		// Ignore remove errors
	}
};

export const getLeadImportProgress = (userId: number): LeadImportProgress | null => {
	try {
		const raw = window.localStorage.getItem(getProgressKey(userId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as LeadImportProgress;
		if (!parsed || typeof parsed.total !== "number") return null;
		return parsed;
	} catch{
		return null;
	}
};

export const setLeadImportProgress = (userId: number, data: LeadImportProgress) => {
	try {
		window.localStorage.setItem(
			getProgressKey(userId),
			JSON.stringify({
				...data,
				sessionId: PAGE_SESSION_ID,
			})
		);
		window.dispatchEvent(new CustomEvent("lead-import-progress"));
	} catch{
		// Ignore write errors
	}
};

export const clearLeadImportProgress = (userId: number) => {
	try {
		window.localStorage.removeItem(getProgressKey(userId));
		window.dispatchEvent(new CustomEvent("lead-import-progress"));
	} catch{
		// Ignore remove errors
	}
};

export const requestCancelLeadImport = (userId: number) => {
	try {
		window.localStorage.setItem(getCancelKey(userId), "true");
	} catch{
		// Ignore write errors
	}
};

export const resetCancelLeadImport = (userId: number) => {
	try {
		window.localStorage.removeItem(getCancelKey(userId));
	} catch{
		// Ignore remove errors
	}
};

export const isCancelLeadImportRequested = (userId: number): boolean => {
	try {
		const value = window.localStorage.getItem(getCancelKey(userId));
		return value === "true";
	} catch{
		return false;
	}
};

/**
 * Checks if there's stale import progress (indicating interrupted import after refresh)
 * Uses a per-page-load session id so refreshes invalidate the prior import session.
 */
export const hasStaleLeadImportProgress = (userId: number): boolean => {
	const progress = getLeadImportProgress(userId);
	if (!progress || !progress.inProgress) {
		return false;
	}
	if (!progress.sessionId) {
		return true;
	}
	return progress.sessionId !== PAGE_SESSION_ID;
};

/**
 * Clears stale import progress if detected. Should be called on component mount
 * to clean up interrupted imports after page refresh.
 */
export const clearStaleLeadImportProgress = (userId: number) => {
	if (hasStaleLeadImportProgress(userId)) {
		clearLeadImportProgress(userId);
		resetCancelLeadImport(userId);

		// Clear incomplete import data so there's no highlighting or alert
		const recentImport = getRecentLeadImport(userId);
		if (recentImport && !recentImport.completed) {
			clearRecentLeadImport(userId);
		}
	}
};
